"use client";

import { useQueryClient } from "@tanstack/react-query";
import GenericBlurredPlaceholder from "@/app/(components)/generics/BlurredPlaceholder/BlurredPlaceholder";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import TabbedContent from "@/app/(components)/generics/TabbedContent/TabbedContent";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useCanEditContent } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { updateAvatarField } from "@/app/actions/client-avatar/update/action";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { AvatarDetailProps } from "@/app/types/client-avatar/AvatarDetailProps";
import { AvatarField, UserClientAvatar } from "@/app/types/client-avatar/UserClientAvatar";
import { Limits } from "@/app/vars/Limits";
import { QueryKey } from "@/app/vars/QueryKeys";
import AvatarInitials from "../Avatars/components/AvatarInitials";
import styles from "./AvatarDetail.module.css";
import AvatarDetailAbout from "./components/AvatarDetailAbout";
import AvatarDetailFit from "./components/AvatarDetailFit";
import AvatarDetailObjections from "./components/AvatarDetailObjections";
import AvatarDetailPain from "./components/AvatarDetailPain";
import AvatarDetailSituation from "./components/AvatarDetailSituation";
import AvatarDetailVerbatims from "./components/AvatarDetailVerbatims";

export default function AvatarDetail(props: AvatarDetailProps) {
  const T = useT();
  const canEdit = useCanEditContent();
  const {
    params,
    avatar,
    isPlaceholder: isPlaceholderProp,
    autoEditField,
    isAnalyzing = false,
    actions,
  } = props;
  const queryClient = useQueryClient();

  const user = useAuthUser();
  const isPlaceholder = isPlaceholderProp ?? !user;

  const handleSave = async (fieldKey: AvatarField, val: string) => {
    queryClient.setQueryData<UserClientAvatar[]>([QueryKey.CLIENT_AVATAR, avatar.offerId], (prev) =>
      prev?.map((a) => (a.id === avatar.id ? ({ ...a, [fieldKey]: val } as UserClientAvatar) : a)),
    );

    try {
      await updateAvatarField(avatar.offerId, fieldKey, val);
      queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_AVATAR, avatar.offerId] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_AVATAR, "detail", avatar.id] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
    } catch (error) {
      console.error("Failed to update avatar field", error);
      queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_AVATAR, avatar.offerId] });
    }
  };

  const items = [
    {
      id: "about",
      title: T(TKey.CLIENT_AVATAR_DETAIL_TAB_ABOUT) as string,
      content: <AvatarDetailAbout params={params} avatar={avatar} isAnalyzing={isAnalyzing} />,
    },
    {
      id: "pain",
      title: T(TKey.CLIENT_AVATAR_DETAIL_TAB_PAIN) as string,
      content: <AvatarDetailPain params={params} avatar={avatar} isAnalyzing={isAnalyzing} />,
    },
    {
      id: "situation",
      title: T(TKey.CLIENT_AVATAR_DETAIL_TAB_SITUATION) as string,
      content: <AvatarDetailSituation params={params} avatar={avatar} isAnalyzing={isAnalyzing} />,
    },
    {
      id: "objections",
      title: T(TKey.CLIENT_AVATAR_DETAIL_TAB_OBJECTIONS) as string,
      content: <AvatarDetailObjections params={params} avatar={avatar} isAnalyzing={isAnalyzing} />,
    },
    {
      id: "verbatims",
      title: T(TKey.CLIENT_AVATAR_DETAIL_TAB_VERBATIMS) as string,
      content: <AvatarDetailVerbatims params={params} avatar={avatar} isAnalyzing={isAnalyzing} />,
    },
    {
      id: "fit",
      title: T(TKey.CLIENT_AVATAR_DETAIL_TAB_FIT) as string,
      content: <AvatarDetailFit params={params} avatar={avatar} isAnalyzing={isAnalyzing} />,
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.headerLeft}>
            {isPlaceholder ? (
              <div className={styles.avatarPlaceholder} />
            ) : (
              <AvatarInitials name={avatar.name} large />
            )}
            <div
              className={styles.headerMeta}
              style={isPlaceholder ? { flex: 1, minWidth: 0 } : {}}
            >
              {isPlaceholder ? (
                <GenericBlurredPlaceholder
                  className={styles.genericPlaceholder}
                  rowClassName={styles.genericRow}
                  rows={[
                    { cells: [{ width: "60%" }] },
                    { cells: [{ width: "45%" }] },
                    { cells: [{ width: "35%" }] },
                  ]}
                />
              ) : (
                <>
                  <EditableText
                    value={avatar.name}
                    onSave={(val) => handleSave(AvatarField.NAME, val)}
                    className={styles.headerName}
                    placeholder={T(TKey.CLIENT_AVATAR_CARD_NAME_PLACEHOLDER)}
                    startEditingOnMount={autoEditField === AvatarField.NAME}
                    maxLength={Limits.MAX_AVATAR_SHORT_TEXT}
                    disabled={isAnalyzing || !canEdit}
                  />
                  <EditableText
                    value={avatar.role}
                    onSave={(val) => handleSave(AvatarField.ROLE, val)}
                    className={styles.headerRole}
                    placeholder={T(TKey.CLIENT_AVATAR_CARD_ROLE_PLACEHOLDER)}
                    startEditingOnMount={autoEditField === AvatarField.ROLE}
                    maxLength={Limits.MAX_AVATAR_SHORT_TEXT}
                    disabled={isAnalyzing || !canEdit}
                  />
                </>
              )}
            </div>
          </div>

          {actions && <div className={styles.headerActions}>{actions}</div>}
        </div>
      </div>

      <TabbedContent items={items} disableScrollOnTabChange={true} />
    </div>
  );
}
